import React from 'react';
import { ModelStatsMap } from '../../../types/analytics.types';
import { MODEL_KEYS, MODEL_LABELS } from '../../../config/analytics';
import { fmt, formatCurrency, formatHoldingTime } from '../../../utils/formatters';

interface Props {
  selectedModel: string;
  statsMap: ModelStatsMap;
  onSelectModel: (modelKey: string) => void;
}

const ModelDetailKPIs: React.FC<Props> = ({ selectedModel, statsMap, onSelectModel }) => {
  const activeModels = MODEL_KEYS.filter(k => statsMap[k] && statsMap[k].totalTrades > 0);
  const stats = statsMap[selectedModel];

  if (!stats || stats.totalTrades === 0 || activeModels.length === 0) return null;

  const grossWins = stats.totalPL - stats.totalLosses;

  return (
    <div className="analytics-section">
      <div className="kpi-section-header">
        <h3 className="analytics-section-title" style={{ margin: 0 }}>Detailed Model KPIs</h3>
        <select
          className="model-dropdown"
          value={selectedModel}
          onChange={(e) => onSelectModel(e.target.value)}
        >
          {activeModels.map(k => (
            <option key={k} value={k}>{MODEL_LABELS[k] || k}</option>
          ))}
        </select>
      </div>
      <div className="kpi-grid">
        <div className="kpi-card">
          <span className="kpi-label">Total Trades</span>
          <span className="kpi-value">{fmt(stats.totalTrades, 0)}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Trades Won</span>
          <span className="kpi-value positive">{fmt(stats.wins, 0)}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Trades Lost</span>
          <span className="kpi-value negative">{fmt(stats.losses, 0)}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Win Rate</span>
          <span className={`kpi-value ${stats.winRate >= 50 ? 'positive' : 'negative'}`}>
            {fmt(stats.winRate, 1)}%
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Avg Per Win</span>
          <span className="kpi-value positive">${fmt(stats.avgPerWin)}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Avg Per Loss</span>
          <span className="kpi-value negative">${fmt(stats.avgPerLoss)}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Profit Factor</span>
          <span className={`kpi-value ${stats.profitFactor >= 1 ? 'positive' : 'negative'}`}>
            {fmt(stats.profitFactor)}
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Largest Win</span>
          <span className="kpi-value positive">${fmt(stats.largestWin)}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Largest Loss</span>
          <span className="kpi-value negative">${fmt(stats.largestLoss)}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Gross Wins</span>
          <span className="kpi-value positive">${fmt(grossWins)}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Gross Losses</span>
          <span className="kpi-value negative">${fmt(stats.totalLosses)}</span>
        </div>
        <div className="kpi-card highlight">
          <span className="kpi-label">Net P/L</span>
          <span className={`kpi-value ${stats.totalPL >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(stats.totalPL)}
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Expectancy</span>
          <span className={`kpi-value ${stats.expectancy >= 0 ? 'positive' : 'negative'}`}>
            ${fmt(stats.expectancy)}
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Risk/Reward</span>
          <span className={`kpi-value ${stats.riskRewardRatio >= 1 ? 'positive' : 'negative'}`}>
            {fmt(stats.riskRewardRatio)}
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Avg Return/Trade</span>
          <span className={`kpi-value ${stats.avgReturnPerTrade >= 0 ? 'positive' : 'negative'}`}>
            ${fmt(stats.avgReturnPerTrade)}
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Avg Hold (Wins)</span>
          <span className="kpi-value positive">{formatHoldingTime(stats.avgHoldingTimeWins)}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Avg Hold (Losses)</span>
          <span className="kpi-value negative">{formatHoldingTime(stats.avgHoldingTimeLosses)}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Avg Hold (All)</span>
          <span className="kpi-value">{formatHoldingTime(stats.avgHoldingTimeAll)}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Avg Return/Unit</span>
          <span className={`kpi-value ${stats.avgReturnPerUnit >= 0 ? 'positive' : 'negative'}`}>
            ${fmt(stats.avgReturnPerUnit)}
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Avg Win/Unit</span>
          <span className="kpi-value positive">${fmt(stats.avgWinPerUnit)}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Avg Loss/Unit</span>
          <span className="kpi-value negative">${fmt(stats.avgLossPerUnit)}</span>
        </div>
      </div>
    </div>
  );
};

export default ModelDetailKPIs;
